import { useState, useEffect } from 'react';
import sectionItemService from '../../../services/sectionItemService';
import { getServicesForCustomer } from '../../../services/serviceService';
// @ts-ignore
import uploadService from '../../../services/uploadService';

interface SectionItem {
  id: string;
  sectionType: 'businessChallenges' | 'whyChoose' | 'process' | 'solutions' | 'services' | 'testimonials' | 'features' | 'benefits';
  icon: string;
  title: string;
  description: string;
  step?: string;
  keyword?: string;
  link?: string;
  badge?: string;
  image?: string;
  circleColor?: string;
  iconBg?: string;
  iconColor?: string;
  color?: string;
  features?: string[];
  pageContext?: 'home' | 'service';
  pageSlug?: string;
  order: number;
  isActive: boolean;
  isFeatured: boolean;
  additionalData?: any;
}

const STORAGE_KEY = 'sectionItemsFilters';

// Helper functions for localStorage
const loadFiltersFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading filters from localStorage:', error);
  }
  return {
    selectedSectionType: 'all',
    selectedPageContext: 'home',
    selectedServiceSlug: ''
  };
};

const saveFiltersToStorage = (filters: { selectedSectionType: string; selectedPageContext: string; selectedServiceSlug: string }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Error saving filters to localStorage:', error);
  }
};

const SectionItemsManagement = () => {
  const [items, setItems] = useState<SectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SectionItem | null>(null);
  
  // Load filters from localStorage on mount
  const initialFilters = loadFiltersFromStorage();
  const [selectedSectionType, setSelectedSectionType] = useState<string>(initialFilters.selectedSectionType);
  const [selectedPageContext, setSelectedPageContext] = useState<string>(initialFilters.selectedPageContext);
  const [selectedServiceSlug, setSelectedServiceSlug] = useState<string>(initialFilters.selectedServiceSlug);
  const [services, setServices] = useState<Array<{ slug: string; name: string }>>([]);
  const [formData, setFormData] = useState<Partial<SectionItem>>({
    sectionType: 'businessChallenges',
    icon: '',
    title: '',
    description: '',
    step: '',
    keyword: '',
    link: '',
    badge: '',
    image: '',
    circleColor: '',
    iconBg: '',
    iconColor: '',
    color: '',
    features: [],
    pageContext: 'home',
    pageSlug: '',
    order: 0,
    isActive: true,
    isFeatured: false
  });
  const [saving, setSaving] = useState(false);
  const [featureInput, setFeatureInput] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const sectionTypes = [
    { value: 'all', label: 'Tất cả loại' },
    { value: 'businessChallenges', label: 'Thách thức' },
    { value: 'whyChoose', label: 'Tại sao chọn' },
    { value: 'process', label: 'Quy trình' },
    { value: 'solutions', label: 'Giải pháp' },
    { value: 'services', label: 'Dịch vụ' },
    { value: 'testimonials', label: 'Testimonials' },
    { value: 'features', label: 'Tính năng' },
    { value: 'benefits', label: 'Lợi ích' }
  ];

  // Common icon options
  const iconOptions = [
    'ri-link-unlink', 'ri-stack-line', 'ri-refresh-line', 'ri-shield-cross-line',
    'ri-team-line', 'ri-shield-check-line', 'ri-customer-service-line', 'ri-money-dollar-circle-line',
    'ri-search-eye-line', 'ri-lightbulb-flash-line', 'ri-settings-3-line', 'ri-check-line'
  ];

  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveFiltersToStorage({
      selectedSectionType,
      selectedPageContext,
      selectedServiceSlug
    });
  }, [selectedSectionType, selectedPageContext, selectedServiceSlug]);

  useEffect(() => {
    loadItems();
  }, [selectedSectionType, selectedPageContext, selectedServiceSlug]);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      // Use getServicesForCustomer to get all services with parent-child structure
      const response = await getServicesForCustomer();
      
      if (response && response.success) {
        // Response structure: { success: true, data: { services: [...] } }
        let servicesList: any[] = [];
        
        // Try different response structures
        if (response.data) {
          if (Array.isArray(response.data)) {
            servicesList = response.data;
          } else if (response.data.services && Array.isArray(response.data.services)) {
            servicesList = response.data.services;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            servicesList = response.data.items;
          }
        }
        
        // If still empty, check if services are directly in response
        if (servicesList.length === 0 && Array.isArray(response.services)) {
          servicesList = response.services;
        }
        
        // Flatten services to get all slugs (include both parent and children)
        const allServices: Array<{ slug: string; name: string }> = [];
        
        const flattenServices = (services: any[]) => {
          services.forEach((service: any) => {
            // Only add if service has slug and name (filter out empty objects)
            if (service && typeof service === 'object' && service.slug && service.name) {
              allServices.push({ slug: service.slug, name: service.name });
            }
            // Also check for children if they exist
            if (service && service.children && Array.isArray(service.children) && service.children.length > 0) {
              flattenServices(service.children);
            }
          });
        };
        
        if (servicesList.length > 0) {
          flattenServices(servicesList);
          // Sort by name for better UX
          allServices.sort((a, b) => a.name.localeCompare(b.name));
          setServices(allServices);
        } else {
          console.warn('No services found in response:', response);
          setServices([]);
        }
      } else {
        console.warn('Response not successful:', response);
        setServices([]);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      // Set empty array on error
      setServices([]);
    }
  };

  const loadItems = async (showLoading = false) => {
    try {
      // Only show loading spinner on initial load or when explicitly requested
      if (showLoading || isInitialLoad) {
        setLoading(true);
      }
      setError(null);
      const sectionType = selectedSectionType === 'all' ? null : selectedSectionType;
      const response = await sectionItemService.getAll(sectionType);
      if (response.success && response.data) {
        // Filter by pageContext and pageSlug
        let filteredData = response.data;
        filteredData = filteredData.filter((item: SectionItem) => {
          if (selectedPageContext === 'home') {
            return item.pageContext === 'home';
          } else if (selectedPageContext === 'service') {
            // Filter by specific service slug
            if (selectedServiceSlug) {
              return item.pageContext === 'service' && item.pageSlug === selectedServiceSlug;
            }
            return false;
          }
          return false;
        });
        setItems(filteredData);
      } else {
        setError(response.message || 'Không thể tải danh sách mục nội dung');
      }
    } catch (err: any) {
      console.error('Error loading items:', err);
      const errorMessage = err.response?.data?.message 
        || err.message 
        || 'Có lỗi xảy ra khi tải dữ liệu';
      setError(errorMessage);
    } finally {
      if (showLoading || isInitialLoad) {
        setLoading(false);
        setIsInitialLoad(false); // Mark initial load as complete
      }
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      sectionType: selectedSectionType !== 'all' ? selectedSectionType as any : 'businessChallenges',
      icon: '',
      title: '',
      description: '',
      step: '',
      keyword: '',
      link: '',
      badge: '',
      image: '',
      circleColor: '',
      iconBg: '',
      color: '',
      features: [],
      pageContext: 'home',
      pageSlug: '',
      order: items.length > 0 ? Math.max(...items.map(i => i.order)) + 1 : 0,
      isActive: true,
      isFeatured: false
    });
    setFeatureInput('');
    setSelectedImage(null);
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (item: SectionItem) => {
    setEditingItem(item);
    setFormData({
      ...item,
      features: item.features || [],
      pageContext: item.pageContext || 'home',
      pageSlug: item.pageSlug || ''
    });
    setFeatureInput('');
    setSelectedImage(null);
    setImagePreview(item.image || '');
    setShowModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const data = {
        ...formData,
        features: formData.features || []
      };

      let response;
      if (editingItem) {
        response = await sectionItemService.update(editingItem.id, data);
      } else {
        response = await sectionItemService.create(data);
      }
      
      if (response.success && response.data) {
        const itemData = Array.isArray(response.data) ? response.data[0] : response.data;
        const sectionItemId = (itemData as any)?._id || (itemData as any)?.id || editingItem?.id;
        
        // Upload image if selected
        if (selectedImage && sectionItemId) {
          try {
            setUploadingImage(true);
            const uploadResponse = await uploadService.uploadFile(selectedImage, sectionItemId, 'SectionItem');
            
            // Get the uploaded image URL from the response
            // Response structure: { success: true, data: { url: "...", publicUrl: "...", ... } }
            if (uploadResponse && uploadResponse.data) {
              const imageUrl = uploadResponse.data.url || uploadResponse.data.publicUrl;
              
              if (imageUrl) {
                // Update SectionItem with the image URL
                await sectionItemService.update(sectionItemId, { image: imageUrl });
              }
            }
          } catch (uploadError: any) {
            console.error('Error uploading image:', uploadError);
            // Don't fail the whole operation if image upload fails
            // Just show a warning
            setError(`Lưu thành công nhưng upload ảnh thất bại: ${uploadError.message || 'Lỗi không xác định'}`);
          } finally {
            setUploadingImage(false);
          }
        }
        
        await loadItems(false); // Reload without showing loading spinner
        setShowModal(false);
        setEditingItem(null);
        setSelectedImage(null);
        setImagePreview('');
      } else {
        setError(response.message || (editingItem ? 'Cập nhật thất bại' : 'Tạo mới thất bại'));
      }
    } catch (err: any) {
      console.error(`Error ${editingItem ? 'updating' : 'creating'} item:`, err);
      setError(err.response?.data?.message || `Có lỗi xảy ra khi ${editingItem ? 'cập nhật' : 'tạo mới'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa mục nội dung này?')) {
      return;
    }

    try {
      setError(null);
      const response = await sectionItemService.delete(id);
      
      if (response.success) {
        await loadItems(false); // Reload without showing loading spinner
      } else {
        setError(response.message || 'Xóa thất bại');
      }
    } catch (err: any) {
      console.error('Error deleting item:', err);
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi xóa');
    }
  };

  const toggleActive = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    try {
      setError(null);
      const response = await sectionItemService.update(id, { isActive: !item.isActive });
      
      if (response.success) {
        await loadItems(false); // Reload without showing loading spinner
      } else {
        setError(response.message || 'Cập nhật trạng thái thất bại');
      }
    } catch (err: any) {
      console.error('Error toggling active:', err);
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...(formData.features || []), featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const removeFeature = (index: number) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures.splice(index, 1);
    setFormData({ ...formData, features: newFeatures });
  };

  const filteredItems = items;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quản lý Nội dung Section</h2>
          <div className="text-sm text-gray-600 mt-1">
            Quản lý các items trong Business Challenges, Why Choose, Process và các section khác
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          Thêm mới
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Lọc theo loại:</label>
            <select
              value={selectedSectionType}
              onChange={(e) => setSelectedSectionType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            >
              {sectionTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Lọc theo trang:</label>
            <select
              value={selectedPageContext === 'service' && selectedServiceSlug ? `service:${selectedServiceSlug}` : selectedPageContext}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'home') {
                  setSelectedPageContext('home');
                  setSelectedServiceSlug('');
                } else if (value.startsWith('service:')) {
                  const slug = value.replace('service:', '');
                  setSelectedPageContext('service');
                  setSelectedServiceSlug(slug);
                } else {
                  setSelectedPageContext('home');
                  setSelectedServiceSlug('');
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm min-w-[250px]"
            >
              <option value="home">Trang chủ</option>
              {services.length > 0 && (
                services.map((service) => (
                  <option key={service.slug} value={`service:${service.slug}`}>
                    {service.name}
                  </option>
                ))
              )}
              {services.length === 0 && (
                <option value="" disabled>Đang tải services...</option>
              )}
            </select>
          </div>
          <div className="text-sm text-gray-600">
            Tổng: {filteredItems.length} mục
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Icon & Tiêu đề
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loại
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mô tả
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thứ tự
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Chưa có mục nội dung nào
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                          item.iconBg 
                            ? (item.iconBg.includes('from-') || item.iconBg.includes('to-') 
                                ? `bg-gradient-to-br ${item.iconBg}` 
                                : item.iconBg)
                            : 'bg-gray-200'
                        }`}>
                          <i className={`${item.icon} text-lg ${item.iconColor || item.color || 'text-gray-700'}`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.title}</div>
                          {item.step && (
                            <div className="text-xs text-gray-500">Step: {item.step}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {sectionTypes.find(t => t.value === item.sectionType)?.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {item.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.pageContext === 'home' 
                            ? 'bg-purple-100 text-purple-800'
                            : item.pageContext === 'service'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.pageContext === 'home' 
                            ? 'Trang chủ' 
                            : item.pageContext === 'service'
                            ? 'Service'
                            : 'Tất cả'}
                        </span>
                        {item.pageSlug && (
                          <span className="text-xs text-gray-600 truncate max-w-[150px]" title={item.pageSlug}>
                            {item.pageSlug}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(item.id)}
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer whitespace-nowrap ${
                          item.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {item.isActive ? 'Hoạt động' : 'Tạm dừng'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.order}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editingItem ? 'Chỉnh sửa Mục nội dung' : 'Thêm Mục nội dung mới'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Section Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loại Section *
                </label>
                <select
                  value={formData.sectionType}
                  onChange={(e) => setFormData({ ...formData, sectionType: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {sectionTypes.filter(t => t.value !== 'all').map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Icon */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (RemixIcon class) *
                </label>
                <div className="flex gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#2A4B7C] rounded-lg flex-shrink-0">
                    <i className={`${formData.icon || 'ri-question-line'} text-xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      required
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="vd: ri-service-line"
                    />
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  <a 
                    href="https://remixicon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#2A4B7C] hover:text-[#00c897] font-medium inline-flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>
                    Xem danh sách icon tại remixicon.com
                  </a>
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tiêu đề *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Nhập tiêu đề"
                  maxLength={200}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Nhập mô tả chi tiết"
                  maxLength={1000}
                />
              </div>

              {/* Step & Keyword (for Process) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Step (cho Process)
                  </label>
                  <input
                    type="text"
                    value={formData.step || ''}
                    onChange={(e) => setFormData({ ...formData, step: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="01, 02, ..."
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Keyword (cho Process)
                  </label>
                  <input
                    type="text"
                    value={formData.keyword || ''}
                    onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Business-first..."
                    maxLength={100}
                  />
                </div>
              </div>

              {/* Link & Badge */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link
                  </label>
                  <input
                    type="text"
                    value={formData.link || ''}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="/giai-phap/xxx"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Badge
                  </label>
                  <input
                    type="text"
                    value={formData.badge || ''}
                    onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Badge text"
                    maxLength={50}
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hình ảnh
                </label>
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    />
                  </div>
                  {imagePreview && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden border">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                {selectedImage && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ Đã chọn: {selectedImage.name}
                  </p>
                )}
                {formData.image && !selectedImage && (
                  <p className="text-sm text-gray-500 mt-1">
                    Ảnh hiện tại: <a href={formData.image} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">{formData.image}</a>
                  </p>
                )}
                {!selectedImage && !formData.image && (
                  <p className="text-sm text-gray-500 mt-1">
                    Chọn ảnh để upload hoặc nhập URL ảnh bên dưới
                  </p>
                )}
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hoặc nhập Image URL
                  </label>
                  <input
                    type="text"
                    value={formData.image || ''}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="https://... hoặc /stackway/images/..."
                  />
                </div>
              </div>

              {/* Colors (for Process) */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Circle Color
                  </label>
                  <input
                    type="text"
                    value={formData.circleColor || ''}
                    onChange={(e) => setFormData({ ...formData, circleColor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="bg-cyan-400"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon BG
                  </label>
                  <input
                    type="text"
                    value={formData.iconBg || ''}
                    onChange={(e) => setFormData({ ...formData, iconBg: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="bg-cyan-500 or from-red-500/10 to-orange-500/10"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon Color
                  </label>
                  <input
                    type="text"
                    value={formData.iconColor || ''}
                    onChange={(e) => setFormData({ ...formData, iconColor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="text-red-500"
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <input
                    type="text"
                    value={formData.color || ''}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="text-white"
                    maxLength={50}
                  />
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Features (cho Solutions/Services)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    placeholder="Nhập feature và nhấn Enter"
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors cursor-pointer"
                  >
                    Thêm
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(formData.features || []).map((feature, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-red-600 hover:text-red-800 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Page Context & Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trang sử dụng
                </label>
                <select
                  value={formData.pageContext === 'service' && formData.pageSlug ? `service:${formData.pageSlug}` : (formData.pageContext || 'home')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'home') {
                      setFormData({ ...formData, pageContext: 'home', pageSlug: '' });
                    } else if (value.startsWith('service:')) {
                      const slug = value.replace('service:', '');
                      setFormData({ ...formData, pageContext: 'service', pageSlug: slug });
                    } else {
                      setFormData({ ...formData, pageContext: 'home', pageSlug: '' });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="home">Trang chủ</option>
                  {services.length > 0 && (
                    services.map((service) => (
                      <option key={service.slug} value={`service:${service.slug}`}>
                        {service.name}
                      </option>
                    ))
                  )}
                  {services.length === 0 && (
                    <option value="" disabled>Đang tải services...</option>
                  )}
                </select>
                {formData.pageContext === 'service' && formData.pageSlug && (
                  <p className="text-xs text-gray-500 mt-1">
                    Service slug: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{formData.pageSlug}</span>
                  </p>
                )}
              </div>

              {/* Order & Status */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Thứ tự
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trạng thái
                  </label>
                  <select
                    value={formData.isActive ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="true">Hoạt động</option>
                    <option value="false">Tạm dừng</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Featured
                  </label>
                  <select
                    value={formData.isFeatured ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="false">Không</option>
                    <option value="true">Có</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
              >
                Hủy
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploadingImage}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving || uploadingImage ? (uploadingImage ? 'Đang upload ảnh...' : 'Đang lưu...') : (editingItem ? 'Cập nhật' : 'Thêm mới')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionItemsManagement;
